import {
  Box,
  Paper,
  Button,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Header } from './components/Header';
import { useEffect, useState } from 'react';
import {
  createStation,
  updateStation,
  deleteStation,
  updateUnitDirect,
} from '../../functions/stationsAndUnitsApi';
import { useDispatch } from 'react-redux';
import { stationsAndUnitsThunk } from '../../store/stationsAndUnitsReducer/thunk';
import { useSelector } from 'react-redux';
import {
  selectStationsData,
  selectAllUnitsData,
} from '../../store/stationsAndUnitsReducer/selectors';
import StationForm from './components/StationForm';
import Units from './components/Units';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';

export const StationAndUnitsForm = () => {
  const dispatch = useDispatch();
  const { stations, isStationsLoading } = useSelector(selectStationsData);
  const { units, isUnitsLoading } = useSelector(selectAllUnitsData);
  const [showForm, setShowForm] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showUnits, setShowUnits] = useState(false);

  useEffect(() => {
    dispatch(stationsAndUnitsThunk.getStations());
    dispatch(stationsAndUnitsThunk.getStationsStatesThunk());
    dispatch(stationsAndUnitsThunk.getUnitsTypesThunk());
    dispatch(stationsAndUnitsThunk.getAllUnitsThunk({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateStation = () => {
    setSelectedStation(null);
    setIsEditMode(false);
    setShowForm(true);
    setShowUnits(false);
  };

  const handleEditStation = (station) => {
    setSelectedStation(station);
    setIsEditMode(true);
    setShowForm(true);
    setShowUnits(false);
  };

  const handleViewUnits = (station) => {
    setSelectedStation(station);
    setShowUnits(true);
    setShowForm(false);
  };

  const handleDeleteStation = async (station) => {
    try {
      await deleteStation(station.id);
      toast.success('Station deleted successfully');
      dispatch(stationsAndUnitsThunk.getStations());
    } catch (error) {
      toast.error('Failed to delete station');
      console.error('Error deleting station:', error);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (isEditMode && selectedStation) {
        await updateStation(formData, selectedStation.id);
        toast.success('Station updated successfully');
      } else {
        await createStation(formData);
        toast.success('Station created successfully');
      }

      dispatch(stationsAndUnitsThunk.getStations());
      setShowForm(false);
      setSelectedStation(null);
    } catch (error) {
      toast.error(
        isEditMode ? 'Failed to update station' : 'Failed to create station'
      );
      console.error('Error saving station:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedStation(null);
    setIsEditMode(false);
  };

  const handleBackToStations = () => {
    setShowUnits(false);
    setSelectedStation(null);
  };

  const handleUnitStationChange = async (unit, newStation) => {
    try {
      await updateUnitDirect({ station_id: newStation?.id || null }, unit.id);

      dispatch(stationsAndUnitsThunk.getAllUnitsThunk({}));

      toast.success(
        newStation
          ? `Unit connected to station: ${newStation.station_id} - ${newStation.internal_id}`
          : 'Unit disconnected from station'
      );
    } catch (error) {
      toast.error('Failed to connect unit to station');
      console.error('Error connecting unit to station:', error);
    }
  };

  const stationOptions =
    stations?.map((station) => ({
      id: station.id,
      label: `${station.station_id} - ${station.internal_id}`,
      station_id: station.station_id,
      internal_id: station.internal_id,
    })) || [];

  return (
    <Box
      sx={{
        px: '16px',
        pt: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Header
            title={
              showUnits
                ? `Units - ${selectedStation?.internal_id || selectedStation?.station_id}`
                : 'Stations & Units'
            }
            buttonText={showUnits ? undefined : 'Sync Station'}
            onClick={showUnits ? undefined : () => {
              // Sync functionality
              toast.info('Sync functionality coming soon');
            }}
          />
          {showUnits && (
            <ButtonTheme
              onClick={handleBackToStations}
              variant="outlined"
              color="primary"
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Back to Stations
            </ButtonTheme>
          )}
          {!showForm && !showUnits && (
            <ButtonTheme
              disabled
              onClick={handleCreateStation}
              variant="filled"
              color="primary"
              startIcon={<AddIcon />}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Create Station
            </ButtonTheme>
          )}
        </Box>

        {showUnits ? (
          <Units station={selectedStation} />
        ) : !showForm ? (
          <>
            {/* Stations Section */}
            <Paper
              sx={{
                p: '24px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: 1,
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Stations
              </Typography>
              {stations && stations.length > 0 ? (
                <Box>
                  {stations.map((station) => (
                    <Paper
                      key={station.id}
                      sx={{
                        p: 3,
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'primary.main',
                          transform: 'translateY(-1px)',
                          boxShadow: 2,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '1.1rem',
                              mb: 0.5,
                            }}
                          >
                            {station.internal_id || station.station_id}
                          </Box>
                          <Box
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {station.address_line_1 &&
                              `${station.address_line_1}, `}
                            {station.city && `${station.city}, `}
                            {station.state}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewUnits(station)}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: 'none',
                              fontWeight: 500,
                            }}
                          >
                            View Units
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEditStation(station)}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: 'none',
                              fontWeight: 500,
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            disabled
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteStation(station)}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: 'none',
                              fontWeight: 500,
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}
                >
                  {isStationsLoading
                    ? 'Loading stations...'
                    : 'No stations found'}
                </Box>
              )}
            </Paper>

            {/* Units Section */}
            <Paper
              sx={{
                p: '24px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                All Units
              </Typography>
              {units && units.length > 0 ? (
                <Box>
                  {units.map((unit) => (
                    <Paper
                      key={unit.id}
                      sx={{
                        p: 3,
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderColor: 'primary.main',
                          transform: 'translateY(-1px)',
                          boxShadow: 2,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '1.1rem',
                              mb: 0.5,
                            }}
                          >
                            {unit.cad_designation_1 ||
                              unit.cad_designation_2 ||
                              `Unit ${unit.id}`}
                          </Box>
                          <Box
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                            }}
                          >
                            {unit.cad_designation_1 &&
                              unit.cad_designation_2 &&
                              `${unit.cad_designation_1} - ${unit.cad_designation_2}`}
                            {unit.cad_designation_1 &&
                              !unit.cad_designation_2 &&
                              unit.cad_designation_1}
                            {!unit.cad_designation_1 &&
                              unit.cad_designation_2 &&
                              unit.cad_designation_2}
                            {unit.type && ` | Type: ${unit.type}`}
                          </Box>
                          <Box
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.875rem',
                              mt: 0.5,
                            }}
                          >
                            Staffing: {unit.staffing || 0}
                            {unit.dedicated_staffing &&
                              ` | Dedicated Staffing: Yes`}
                            {!unit.dedicated_staffing &&
                              ` | Dedicated Staffing: No`}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minWidth: 300,
                          }}
                        >
                          <Autocomplete
                            options={stationOptions}
                            value={
                              stationOptions.find(
                                (option) => option.id === unit.station_id
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleUnitStationChange(unit, newValue);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Connect to Station"
                                size="small"
                                placeholder="Select station..."
                              />
                            )}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              sx={{
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontWeight: 500,
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              disabled
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              sx={{
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontWeight: 500,
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}
                >
                  {isUnitsLoading ? 'Loading units...' : 'No units found'}
                </Box>
              )}
            </Paper>
          </>
        ) : (
          <StationForm
            station={selectedStation}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isEdit={isEditMode}
          />
        )}
      </Box>
    </Box>
  );
};
